import {
  clearLocalStorage,
  getLocalStorageItem,
  localStorageIsSupported,
  parseJwt,
  setLocalStorageItem,
} from "simplydo/core/helpers";
import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";

import { API, APIError } from ".";

export const auth = (api: API) => ({
  resumeSession(success: () => void, fail: (error: APIError) => void) {
    const token = getLocalStorageItem("auth_token");
    if (!token) {
      return fail?.({ status: 401, message: "Not authenticated" });
    }

    api.token = token;
    const tokenData = parseJwt(token);
    // Times expiry by a thousand to get from milliseconds to seconds
    const expiryDate = tokenData.exp * 1000;
    // Get diff between now, and the expiry
    const daysFromNow = Math.floor(
      (expiryDate - Date.now()) / (1000 * 60 * 60 * 24),
    );
    // If diff is less than 29, we request a new token
    if (daysFromNow >= 29) {
      api.queryClient.invalidateQueries();
      return api.options?.onLogin?.(success, fail);
    }

    this.refreshAuthToken(success, () => {
      api.queryClient.invalidateQueries();

      // On failure we attempt to revert using old token
      api.options?.onLogin?.(success, fail);
    });
  },
  login(
    loginData: {
      email: string;
      password: string;
      otp?: string;
      exchangeToken?: string;
      org?: string;
      challengeResponse?: string;
      utmLink?: string;
      ssoProtectionToken?: string;
      otpRecoveryCode?: string;
    },
    success: (data: OpenAPI.POST<"/accounts/login">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const {
      email,
      password,
      otp,
      exchangeToken,
      org,
      challengeResponse,
      utmLink,
      ssoProtectionToken,
      otpRecoveryCode,
    } = loginData;
    const payload: any = {
      email,
      password,
      otp,
      exchangeToken,
      org,
      challengeResponse,
      utmLink,
      ssoProtectionToken,
      otpRecoveryCode,
    };
    const params = new SearchParams(window?.location?.search ?? {});
    const stateParam = params.get("state");
    if (stateParam) {
      payload.state = stateParam;
    }
    api.unauthenticatedRequest(
      "POST",
      "/accounts/login",
      payload,
      (data) => {
        if (data.token) {
          clearLocalStorage();

          api.token = data.token;

          // On fresh login, fully clear local storage
          if (localStorageIsSupported()) {
            localStorage.clear();
          }
          setLocalStorageItem(api.TOKEN_KEY, data.token);

          api.options?.onLogin?.(() => success(data), fail);
        } else {
          // e.g. when OTP required
          success(data);
        }
      },
      fail,
    );
  },
  loginWithToken(
    token: string,
    success: (data: OpenAPI.POST<"/accounts/login/magic">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/login/magic",
      { token },
      (data) => {
        clearLocalStorage();

        api.token = data.token;

        // On fresh login, fully clear local storage
        if (localStorageIsSupported()) {
          localStorage.clear();
        }
        setLocalStorageItem(api.TOKEN_KEY, data.token);
        api.queryClient.invalidateQueries();

        api.options?.onLogin?.(() => success(data), fail);
      },
      fail,
    );
  },
  requestLoginEmail(
    email: string,
    success: (
      data: OpenAPI.POST<"/accounts/tokens/authorise">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/tokens/authorise",
      { email },
      success,
      fail,
    );
  },
  authoriseToken(
    authenticationToken: string,
    securityPhrase: string,
    success: (
      data: OpenAPI.PUT<"/accounts/tokens/authorise">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "PUT",
      "/accounts/tokens/authorise",
      { authenticationToken, securityPhrase },
      success,
      fail,
    );
  },
  verifyAccount(
    organisation: string,
    email: string,
    challengeResponse: string,
    success: (data: OpenAPI.GET<"/accounts">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "GET",
      `/accounts?org_id=${organisation}&email=${encodeURIComponent(email)}&challenge_response=${challengeResponse}`,
      null,
      success,
      fail,
    );
  },
  register(
    organisation: string,
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    challengeResponse: string,
    agreedTermsVersion: number,
    utmLink: string,
    utmChallenge: string,
    inviteToken: string,
    customTermsVersion: string,
    success: (data: OpenAPI.POST<"/accounts">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts",
      {
        organisation,
        email,
        password,
        firstName,
        lastName,
        challengeResponse,
        agreedTermsVersion,
        utmLink,
        utmChallenge,
        inviteToken,
        customTermsVersion: customTermsVersion ?? "",
      },
      (data) => {
        clearLocalStorage();

        api.token = data.token;

        // On fresh login, fully clear local storage
        if (localStorageIsSupported()) {
          localStorage.clear();
        }
        setLocalStorageItem(api.TOKEN_KEY, data.token);
        api.queryClient.invalidateQueries();

        api.options?.onLogin?.(() => success(data), fail);
      },
      fail,
    );
  },
  logout(success: () => void, fail: (error: APIError) => void) {
    api.authenticatedRequest(
      "POST",
      "/accounts/logout",
      null,
      () => {
        api.token = null;
        api.revertToken = null;

        clearLocalStorage();

        api.options?.onLogout?.();
        api.queryClient.invalidateQueries();
        success?.();
      },
      fail,
    );
  },
  logoutOthers(success: () => void, fail: (error: APIError) => void) {
    api.authenticatedRequest(
      "POST",
      "/accounts/logout/others",
      null,
      success,
      fail,
    );
  },
  sendPasswordResetEmail(
    email: string,
    organisation: string,
    success: (
      data: OpenAPI.POST<"/accounts/password/reset">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/password/reset",
      { email, organisation },
      success,
      fail,
    );
  },
  sendPasswordSetupEmail(
    email: string,
    organisation: string,
    success: (
      data: OpenAPI.POST<"/accounts/password/setup">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/password/setup",
      { email, organisation },
      success,
      fail,
    );
  },
  updatePassword(
    currentPassword: string,
    newPassword: string,
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      "/accounts/password",
      { currentPassword, newPassword },
      success,
      fail,
    );
  },
  adminUpdatePassword(
    userId: string,
    data: OpenAPI.PUT<"/accounts/{id}/password">["requestBody"],
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/accounts/${userId}/password`,
      data,
      success,
      fail,
    );
  },
  updatePasswordWithToken(
    token: string,
    newPassword: string,
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "PUT",
      "/accounts/password",
      { token, newPassword },
      success,
      fail,
    );
  },
  agreeToTerms(
    version: number,
    customTermsVersion: string,
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/accounts/terms/agree`,
      { version, customTermsVersion },
      success,
      fail,
    );
  },
  provideEmail(
    data: OpenAPI.PUT<"/accounts/email/add">["requestBody"],
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", "/accounts/email/add", data, success, fail);
  },
  requestEmailVerification(
    token: string,
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams({ token });
    api.maybeAuthenticatedRequest(
      "POST",
      `/accounts/email/verify${params.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  adminUpdateEmail(
    userId: string,
    data: OpenAPI.PUT<"/accounts/{id}/email">["requestBody"],
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/accounts/${userId}/email`,
      data,
      success,
      fail,
    );
  },
  verifyEmail(
    token: string,
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "PUT",
      "/accounts/email/verify",
      { token },
      success,
      fail,
    );
  },
  getApiTokens(
    success: (data: OpenAPI.GET<"/accounts/api/tokens">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      "/accounts/api/tokens",
      null,
      success,
      fail,
    );
  },
  deleteApiToken(
    tokenId: string,
    tokenIndex: number,
    success: () => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams({ tokenId, tokenIndex });

    api.authenticatedRequest(
      "DELETE",
      `/accounts/api/tokens${params.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  viewExternalApiToken(
    tokenId: string,
    tokenIndex: number,
    success: (
      data: OpenAPI.GET<"/accounts/api/tokens/external">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams({ tokenId, tokenIndex });

    api.authenticatedRequest(
      "GET",
      `/accounts/api/tokens/external${params.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  createExternalApiToken(
    data: OpenAPI.POST<"/accounts/api/tokens/external">["requestBody"],
    success: (
      data: OpenAPI.POST<"/accounts/api/tokens/external">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/accounts/api/tokens/external",
      data,
      success,
      fail,
    );
  },
  deleteExternalApiToken(
    tokenId: string,
    tokenIndex: number,
    success: (
      data: OpenAPI.DELETE<"/accounts/api/tokens/external">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams({ tokenId, tokenIndex });
    api.authenticatedRequest(
      "DELETE",
      `/accounts/api/tokens/external${params.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  refreshAuthToken(
    success: (
      data: OpenAPI.POST<"/accounts/tokens/refresh">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/accounts/tokens/refresh",
      null,
      (newToken) => {
        api.token = newToken;
        setLocalStorageItem(api.TOKEN_KEY, newToken);
        api.queryClient.invalidateQueries();
        api.options?.onLogin?.(() => success?.(newToken), fail);
      },
      fail,
    );
  },
  generateOtpUri(
    success: (data: OpenAPI.POST<"/accounts/mfa">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", "/accounts/mfa", null, success, fail);
  },
  disableOtp(
    data: any,
    success: (data: OpenAPI.DELETE<"/accounts/mfa">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("DELETE", "/accounts/mfa", data, success, fail);
  },
  enableOtp(
    data: any,
    success: (data: OpenAPI.PUT<"/accounts/mfa">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", "/accounts/mfa", data, success, fail);
  },
  updateEmailAddress(
    data: any,
    success: (data: OpenAPI.PUT<"/accounts/email">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", "/accounts/email", data, success, fail);
  },
  generateExchangeToken(
    success: (data: OpenAPI.POST<"/accounts/tokens">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", "/accounts/tokens", null, success, fail);
  },
  addPhoneNumber(
    number: string,
    success: (data: OpenAPI.POST<"/accounts/phone">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/accounts/phone",
      { number },
      success,
      fail,
    );
  },
  removePhoneNumber(
    number: string,
    success: (data: OpenAPI.DELETE<"/accounts/phone">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      "/accounts/phone",
      { number },
      success,
      fail,
    );
  },
  verifyPhoneNumber(
    number: string,
    code: string,
    success: (data: OpenAPI.POST<"/accounts/phone/verify">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/accounts/phone/verify",
      { number, code },
      success,
      fail,
    );
  },
  resendPhoneNumberVerification(
    number: string,
    success: (
      data: OpenAPI.POST<"/accounts/phone/verify/new">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/accounts/phone/verify/new",
      { number },
      success,
      fail,
    );
  },
  requestWebsocketToken(
    success: (
      data: OpenAPI.GET<"/accounts/websocket/token">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      "/accounts/websocket/token",
      null,
      success,
      fail,
    );
  },
  requestWebdriverToken(
    path: string,
    success: (
      data: OpenAPI.GET<"/accounts/webdriver/token">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/accounts/webdriver/token?path=${encodeURIComponent(path)}`,
      null,
      success,
      fail,
    );
  },
  addPushToken(
    data: OpenAPI.POST<"/accounts/push/tokens">["requestBody"],
    success: (data: OpenAPI.POST<"/accounts/push/tokens">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    return api.authenticatedRequest(
      "POST",
      "/accounts/push/tokens",
      data,
      success,
      fail,
    );
  },
  removePushToken(
    data: OpenAPI.DELETE<"/accounts/push/tokens">["requestBody"],
    success: (
      data: OpenAPI.DELETE<"/accounts/push/tokens">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    return api.authenticatedRequest(
      "DELETE",
      "/accounts/push/tokens",
      data,
      success,
      fail,
    );
  },
  addFCMPushToken(
    token: string,
    success: (
      data: OpenAPI.POST<"/accounts/push/fcm_tokens">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    return api.authenticatedRequest(
      "POST",
      "/accounts/push/fcm_tokens",
      { token },
      success,
      fail,
    );
  },
  removeFCMPushToken(
    token: string,
    success: (
      data: OpenAPI.DELETE<"/accounts/push/fcm_tokens">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    return api.authenticatedRequest(
      "DELETE",
      "/accounts/push/fcm_tokens",
      { token },
      success,
      fail,
    );
  },
  reportContent(
    data: any,
    success: (
      data: OpenAPI.POST<"/accounts/reportContent">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.maybeAuthenticatedRequest(
      "POST",
      "/accounts/reportContent",
      data,
      success,
      fail,
    );
  },
});
